import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Divider,
  Stack,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Avatar
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  TrendingUp as TrendingUpIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Mock data for dashboard
const recentTransactions = [
  { id: 1, title: 'New supplies purchased', amount: -125.00, date: '2023-10-15', type: 'expense', category: 'Supplies' },
  { id: 2, title: 'Service payment received', amount: 350.00, date: '2023-10-14', type: 'income', category: 'Services' },
  { id: 3, title: 'Monthly rent', amount: -1200.00, date: '2023-10-10', type: 'expense', category: 'Rent' },
  { id: 4, title: 'Product sale', amount: 89.50, date: '2023-10-07', type: 'income', category: 'Products' },
];

const monthlyData = {
  income: 8750.00,
  expenses: 5320.00,
  profit: 3430.00,
  lastMonth: {
    income: 7950.00,
    expenses: 5100.00,
    profit: 2850.00
  }
};

const categorySummary = [
  { name: 'Services', amount: 5600.00, percentage: 64 },
  { name: 'Products', amount: 2350.00, percentage: 27 },
  { name: 'Other', amount: 800.00, percentage: 9 }
];

const goalsData = {
  monthlyTarget: 10000.00,
  currentProgress: 8750.00,
  percentage: 87.5
};

const FinanceDashboard: React.FC = () => {
  const navigate = useNavigate();
  // const { currentUser } = useAuth();

  const handleAddTransaction = () => {
    // Navigate to expense creation form
    navigate('/expenses/new');
  };

  const getIncomeChange = () => {
    const change = ((monthlyData.income - monthlyData.lastMonth.income) / monthlyData.lastMonth.income) * 100;
    return change.toFixed(1);
  };

  const getExpenseChange = () => {
    const change = ((monthlyData.expenses - monthlyData.lastMonth.expenses) / monthlyData.lastMonth.expenses) * 100;
    return change.toFixed(1);
  };

  const getProfitChange = () => {
    const change = ((monthlyData.profit - monthlyData.lastMonth.profit) / monthlyData.lastMonth.profit) * 100;
    return change.toFixed(1);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" fontWeight={600}>Finance Dashboard</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddTransaction}
          sx={{ borderRadius: 2 }}
        >
          New Transaction
        </Button>
      </Box>

      {/* Key Metrics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Box>
          <Card raised={false} sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', mr: 1.5 }}>
                  <MoneyIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>Income</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ my: 1, fontWeight: 700 }}>
                ${monthlyData.income.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color={Number(getIncomeChange()) >= 0 ? 'success.main' : 'error.main'} fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                  {Number(getIncomeChange()) >= 0 ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  {Math.abs(Number(getIncomeChange()))}% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card raised={false} sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#fbe9e7', color: '#d84315', mr: 1.5 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>Expenses</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ my: 1, fontWeight: 700 }}>
                ${monthlyData.expenses.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color={Number(getExpenseChange()) < 0 ? 'success.main' : 'error.main'} fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                  {Number(getExpenseChange()) < 0 ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                  {Math.abs(Number(getExpenseChange()))}% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card raised={false} sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', mr: 1.5 }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>Profit</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ my: 1, fontWeight: 700 }}>
                ${monthlyData.profit.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color={Number(getProfitChange()) >= 0 ? 'success.main' : 'error.main'} fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
                  {Number(getProfitChange()) >= 0 ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  {Math.abs(Number(getProfitChange()))}% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recent Transactions and Monthly Progress */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 3 }}>
        <Box>
          <Card sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Recent Transactions</Typography>
                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box>
              <List>
                {recentTransactions.map((transaction) => (
                  <React.Fragment key={transaction.id}>
                    <ListItem 
                      alignItems="flex-start"
                      secondaryAction={
                        <Typography 
                          variant="body1" 
                          fontWeight={600}
                          color={transaction.type === 'income' ? 'success.main' : 'error.main'}>
                          {transaction.type === 'income' ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                        </Typography>
                      }
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: '40px' }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: transaction.type === 'income' ? '#e8f5e9' : '#ffebee',
                            color: transaction.type === 'income' ? '#2e7d32' : '#c62828'
                          }}
                        >
                          {transaction.type === 'income' ? <TrendingUpIcon fontSize="small" /> : <ShoppingCartIcon fontSize="small" />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={transaction.title}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {transaction.date} • {transaction.category}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button onClick={() => navigate('/expenses')} sx={{ mr: 1 }}>View Expenses</Button>
                <Button onClick={() => navigate('/income')} color="primary">View Income</Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        <Box>
          <Stack spacing={3}>
            {/* Monthly Goal Card */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Monthly Goal</Typography>
                <Box sx={{ mt: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      ${goalsData.currentProgress.toLocaleString()} / ${goalsData.monthlyTarget.toLocaleString()}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={goalsData.percentage} 
                    sx={{ height: 8, borderRadius: 5 }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                    {goalsData.percentage}% Complete
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Income Breakdown Card */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Income Breakdown</Typography>
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {categorySummary.map((category) => (
                    <Box key={category.name}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{category.name}</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          ${category.amount.toLocaleString()} ({category.percentage}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={category.percentage} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 5,
                          bgcolor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: category.name === 'Services' ? '#4caf50' : 
                                    category.name === 'Products' ? '#2196f3' : '#ff9800'
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default FinanceDashboard;